<template>
  <div
    v-if="order.user || currentSite.allowAnonymousOrders"
    class="justify-between no-wrap text-responsive-grey-9"
    :class="[outerDivClass, {
      'checkout-controls': !$kiosk,
      'q-pa-sm': !dense,
      'q-pa-xs': dense,
      'bg-white': isMobileView && !$q.dark.isActive
    }]"
    style="width: 100%; margin-left: auto;"
    :style="{
      'max-width': topbar || $register ? undefined : isCartPageInLandscapeView ? '400px' : '800px',
      'margin-right': isCartPageInLandscapeView ? '0' : ' auto'
    }"
    data-test-id="checkout-controls"
  >
    <div
      v-if="topbar"
      class="col-auto q-pr-md relative-position"
      :class="!isMobileView ? 'q-mr-lg' : 'q-ml-md'"
      :style="{ 'border-right': !isMobileView ? '1px solid #777' : undefined }"
    >
      <q-icon
        flat
        :color="iconColor"
        name="fal fa-cart-shopping"
        :size="!isMobileView ? 'sm' : 'xs'"
      />
    </div>

    <div
      v-if="!hideAmounts && (checkoutData.showInfo || checkoutData.showSubtotal)"
      class="justify-between"
      :class="{
        'col': checkoutData.showInfo && checkoutData.showSubtotal && !stackLayout,
        'col-auto': !checkoutData.showInfo || !checkoutData.showSubtotal || topbar,
        'q-mr-md': !isMobileView && !stackLayout || forceLargeStyle,
        'column': stackLayout,
        'row': !stackLayout
      }"
      :style="{ 'width': checkoutData.showInfo && checkoutData.showSubtotal && !topbar ? '100%' : '' }"
    >
      <div
        v-if="checkoutData.showInfo && !topbar"
        :class="stackLayout ? 'row' : 'column'"
      >
        <div
          :class="{
            'text-h6 col text-grey': stackLayout,
            'text-overline': !isFooter
          }"
          style="line-height: 1;"
        >
          {{ checkoutData.infoTitle }}
        </div>
        <div
          :class="{
            'col-auto text-grey': stackLayout,
            'text-h6': !isFooter,
            'text-grey-7': isFooter
          }"
        >
          {{ checkoutData.info }}
        </div>
      </div>
      <div
        v-if="checkoutData.showSubtotal && !hideSubtotal"
        :class="stackLayout ? 'row' : 'column text-right'"
      >
        <div
          v-if="!topbar"
          :class="stackLayout ? 'text-h6 col' : 'text-overline'"
          style="line-height: 1;"
        >
          {{ $t('pricing.subtotal') }}
        </div>
        <i18n-n
          :value="order.displaySubtotal"
          format="currency"
          class="text-h6"
          :class="{ 'col-auto': stackLayout }"
          tag="span"
          scope="global"
        />
      </div>
    </div>
    <div
      v-if="topbar"
      class="col text-center"
      :class="isMobileView && hideCheckoutButton ? 'text-right' : 'text-center'"
    >
      <q-btn
        v-if="!hideToggleDrawer"
        :color="buttonTextColor"
        flat
        class="q-px-xs"
        :class="showOrderItemsDetails ? 'full-width justify-between' : undefined"
        :size="topbar ? !isMobileView ? 'lg' : 'md' : undefined"
        :icon-right="drawerShown ? 'expand_less' : 'expand_more'"
        @click="$emit('toggle-drawer')"
      >
        <div
          class="col-auto"
          :class="{
            'text-left': showOrderItemsDetails,
            'text-center': !showOrderItemsDetails
          }"
        >
          <template v-if="$register || checkoutData?.showBookingFees">
            <i18n-n
              :value="order.displayTotal"
              format="currency"
              class="text-weight-regular col text-right"
              tag="span"
              scope="global"
            />
            <span
              v-if="checkoutData?.showBookingFees && order.displayBookingFeesTotal > 0"
              class="text-lowercase"
              style="opacity: 0.7;"
            >
              ({{ $t('cart.includeFees') }} <i18n-n
                :value="order.displayBookingFeesTotal"
                format="currency"
                class="text-weight-regular"
              />)
            </span>
          </template>
          <template v-else>
            {{ drawerShown ? $t('cart.hideCart') : $t('cart.viewCart') }}
          </template>
        </div>
        <div
          v-if="showOrderItemsDetails"
          class="row justify-end items-center q-mr-sm col"
        >
          <span
            v-if="!$register && checkoutData.stepTitle === $i18n.t('checkout.steps.seats') "
            class="text-bold col ellipsis text-right"
          > {{ checkoutData.selectedSeats }}</span>
          <span
            v-else-if="order.totalOrderItemsWithoutBookingFee"
            class="text-lowercase"
            :class="{ 'parentheses': $register }"
          >
            {{ $tc('item.itemCount', order.totalOrderItemsWithoutBookingFee.length) }}
          </span>
        </div>
      </q-btn>
    </div>
    <div
      v-if="!hideCheckoutButton"
      :class="{
        'full-width q-mt-md': stackLayout,
        'col': !topbar,
        'col-auto': topbar,
        'text-right': topbar && !isMobileView,
      }"
    >
      <slot>
        <q-btn
          :class="getClasses(buttonType, buttonColor, buttonColorHex)"
          :style="getStyles(buttonType, buttonColor, buttonColorHex, buttonBorderRadius)"
          :loading="checkoutData.showLoading"
          :label="nextButtonLabel"
          :disable="!checkoutData.canProceed"
          :size="topbar ? 'md' : undefined"
          :padding="topbar ? 'sm xl': buttonPadding"
          :rounded="!!buttonBorderRadius"
          :outlined="buttonType === BUTTON_TYPES.OUTLINE"
          :flat="buttonType === BUTTON_TYPES.FLAT"
          class="full-width"
          data-test-id="checkout-controls-next-button"
          @click="next"
        />
      </slot>
    </div>
  </div>
</template>

<script>
import { BUTTON_TYPES } from 'src/models/page/Section'

export default {
  name: 'CheckoutControls',

  inject: ['order', 'checkoutData'],
  props: {
    flat: {
      type: Boolean,
      required: false,
      default: false
    },
    forceLargeStyle: {
      type: Boolean,
      required: false,
      default: false
    },
    hideAmounts: {
      type: Boolean,
      required: false,
      default: false
    },
    hideSubtotal: {
      type: Boolean,
      required: false,
      default: false
    },
    stackLayout: {
      type: Boolean,
      required: false,
      default: false
    },
    topbar: {
      type: Boolean,
      required: false,
      default: false
    },
    drawerShown: {
      type: Boolean,
      required: false,
      default: false
    },
    hideToggleDrawer: {
      type: Boolean,
      required: false,
      default: false
    },
    showOrderItemsDetails: {
      type: Boolean,
      required: false,
      default: false
    },
    zoom: {
      type: Number,
      required: false,
      default: 1
    },
    isCartPage: {
      type: Boolean,
      required: false,
      default: false
    },
    hideCheckoutButton: {
      type: Boolean,
      required: false,
      default: false
    },
    dense: {
      type: Boolean,
      required: false,
      default: false
    },
    isFooter: {
      type: Boolean,
      required: false,
      default: false
    },
    isPhonePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonPadding: {
      type: String,
      required: false,
      default: 'sm'
    },
    buttonType: {
      type: String,
      required: false,
      default: BUTTON_TYPES.UNELEVATED
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    buttonBorderRadius: {
      type: Number,
      required: false,
      default: null
    }
  },
  emits: ['toggle-drawer', 'next'],
  computed: {
    outerDivClass () {
      let smallScreenClass = 'column items-stretch'
      const largeScreenClass = 'row items-center'
      if (!this.flat) {
        smallScreenClass += ' shadow-2'
      }
      return this.isMobileView && !this.forceLargeStyle
        ? smallScreenClass
        : largeScreenClass
    },
    isCartPageInLandscapeView () {
      return this.$isLandscapeView() && this.isCartPage
    },
    isMobileView () {
      return this.$q.screen.lt.sm || this.isPhonePreview
    },
    iconColor () {
      if (this.$isLandscapeView() && !this.isPhonePreview) return 'primary'
      return this.$q.dark.isActive && !this.$isColorDark(this.currentSite.headerColor) && this.isMobileView && !this.$register ? 'dark' : 'responsive'
    },
    buttonTextColor () {
      if (this.$isLandscapeView() && !this.isPhonePreview) return 'primary'
      return this.$q.dark.isActive && !this.$isColorDark(this.currentSite.headerColor) && this.isMobileView && !this.$register ? 'dark' : 'responsive-grey-8'
    },
    nextButtonLabel () {
      if (this.order.orderItems.data.length === 0 && this.checkoutData.showEmptyCartNextLabel) {
        return this.$t('cart.cartEmpty')
      } else if (this.checkoutData.canProceed) {
        return this.checkoutData.nextButtonLabel
      } else {
        return this.checkoutData.unableToProceedNextButtonLabel || this.checkoutData.nextButtonLabel
      }
    }
  },
  created () {
    _.extend(this, {
      BUTTON_TYPES
    })
  },
  configuration: ['currentSite'],
  methods: {
    next () {
      this.$emit('next')
    },
    getClasses (button, color, colorHex) {
      const classes = []

      // Handle STANDARD and UNELEVATED button types
      if (button === BUTTON_TYPES.STANDARD || button === BUTTON_TYPES.UNELEVATED) {
        if (color === 'default') {
          classes.push('text-grey')
        } else if (color !== 'custom') {
          classes.push(`bg-${color}`)
          classes.push(this.$getTextColor(this.currentSite, color))
        } else if (color === 'custom' && colorHex) {
          classes.push(this.$isColorDark(colorHex) ? 'text-white' : 'text-black')
        }
      }

      // Handle FLAT and OUTLINE button types
      if (button === BUTTON_TYPES.FLAT || button === BUTTON_TYPES.OUTLINE) {
        if (color !== 'custom') {
          classes.push(`text-${color}`)
        }
      }

      return classes.join(' ')
    },
    getStyles (button, color, colorHex, borderRadius) {
      const styles = []

      if (borderRadius !== null) {
        styles.push(`border-radius: ${borderRadius}px`)
      }
      if ((button === BUTTON_TYPES.STANDARD || button === BUTTON_TYPES.UNELEVATED) && color === 'custom') {
        styles.push(`background-color: ${colorHex}`)
      }
      if ((button === BUTTON_TYPES.FLAT || button === BUTTON_TYPES.OUTLINE) && color === 'custom') {
        styles.push(`color: ${colorHex}`)
      }
      if (this.zoom !== 1) {
        styles.push(`zoom: ${this.zoom}`)
      }
      if (this.topbar && !this.isMobileView) {
        styles.push('max-width: 280px')
      }

      return styles.join(';')
    }

  }
}
</script>

<style lang="scss" scoped>
  .text-overlay {
    line-height: 1rem;
  }
</style>
